import { useRegisterSW } from "virtual:pwa-register/react";
import { Button, notification } from "antd";
import { useEffect, useState } from "react";

/**
 * A köteg saját build-ideje, a vite.config.ts `define`-ból.
 *
 * Ez kell ahhoz, hogy a felület meg tudja ÁLLAPÍTANI, hogy ő maga régi-e.
 * A version.json-ból csak azt látná, mi van a szerveren.
 */
declare const __BUILD_TIME__: string;

/**
 * Install prompt + auto-update notifier, styled like the szirének/milliomosok
 * app install modal — a centered, app-store-shaped card with icon, name and
 * a big "Telepítés" call to action. iOS Safari doesn't fire the native
 * beforeinstallprompt event, so on that platform we render instructions
 * (Share → Add to Home Screen) with a mini-icon showing the share glyph.
 */
type BeforeInstallPromptEvent = Event & {
	prompt: () => Promise<void>;
	userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const isIosSafari = (() => {
	if (typeof navigator === "undefined") return false;
	const ua = navigator.userAgent;
	const iOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Macintosh") && "ontouchend" in document);
	const webkit = /WebKit/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
	return iOS && webkit;
})();

const isStandalone = () =>
	typeof window !== "undefined" &&
	(window.matchMedia?.("(display-mode: standalone)").matches ||
		// iOS Safari legacy flag
		(navigator as unknown as { standalone?: boolean }).standalone === true);

const DISMISS_KEY = "pwa-install-dismissed-at";
const DISMISS_DAYS = 7; // ask again after a week

function wasRecentlyDismissed(): boolean {
	const raw = localStorage.getItem(DISMISS_KEY);
	if (!raw) return false;
	const at = Number(raw);
	if (!Number.isFinite(at)) return false;
	return Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

export default function PwaPrompts() {
	const {
		needRefresh: [needRefresh, setNeedRefresh],
		updateServiceWorker,
	} = useRegisterSW({
		onRegisteredSW(_url, r) {
			if (!r) return;

			// Óránként kevés: egy telepítés napján órákig futhat valaki régi
			// kötegen. 15 perc + a lap előtérbe kerülése gyakorlatilag azonnal
			// észreveszi az új verziót.
			const ellenoriz = () => {
				r.update().catch(() => {});
			};

			setInterval(ellenoriz, 15 * 60 * 1000);

			const elotérbe = () => {
				if (document.visibilityState === "visible") ellenoriz();
			};

			document.addEventListener("visibilitychange", elotérbe);
			window.addEventListener("focus", elotérbe);
		},
	});

	/*
	 * MÁSODIK RÉTEG: verzió-figyelés a service workertől FÜGGETLENÜL.
	 *
	 * A `needRefresh` a worker életciklusán múlik, ami nem mindenhol
	 * viselkedik egyformán — iOS-en (hazzáadva a kezdőlapra) a rendszer
	 * agresszívan felfüggeszti és visszadobja az alkalmazást, és a worker
	 * frissítése elmaradhat. A `version.json`-t a telepítés minden alkalommal
	 * újraírja, és a service worker NEM gyorsítótárazza (a szabályai
	 * `request.destination` alapján illesztenek, ennek pedig üres) — így ez a
	 * réteg akkor is észreveszi az új verziót, ha a worker hallgat.
	 *
	 * Az „én verziómat" az első lekérés adja: ha később MÁS jön, új telepítés
	 * történt a munkamenet alatt.
	 */
	const [ujVerzio, setUjVerzio] = useState(false);

	useEffect(() => {
		let sajatVerzio: string | null = null;
		let elo = true;

		/*
		 * KÖTELEZŐ FRISSÍTÉS.
		 *
		 * Van olyan telepítés, ami a régi felületet ELTÖRI — például amikor a
		 * fájl-token sémája cserélődött: aki a régi kötegen maradt, annál
		 * egyetlen letöltés sem működött, és nem lehetett mindenkit egyenként
		 * megkérni, hogy nyomjon Ctrl+Shift+R-t. Ilyenkor a telepítés
		 * `REQUIRE_UPDATE=1`-gyel készül, és a version.json
		 * `requiredBuildTime`-ja az adott buildre áll.
		 *
		 * Ha a MI build-időnk ennél régebbi, nem kérdezünk: kiírjuk, hogy
		 * frissítés szükséges, kiürítjük a gyorsítótárakat és újratöltünk.
		 *
		 * ⚠️ Ez SZÁNDÉKOSAN nem a service workerre épül: sima fetch +
		 * caches + reload, tehát ugyanúgy működik telepített alkalmazásként
		 * és sima böngészőlapon is, Chrome-ban és Safariban egyaránt.
		 */
		const kenyszeritettFrissites = async () => {
			/*
			 * Sima DOM-réteg, NEM értesítő könyvtár.
			 *
			 * Ez egy blokkoló üzenet: a felhasználónak látnia kell, mi történik,
			 * mielőtt kicsúszik alóla az oldal. Próbáltam antd-értesítéssel, és
			 * több böngészőmotoron nem jelent meg időben — egy kötelező
			 * tájékoztatás nem függhet szolgáltatói környezettől vagy
			 * betöltési sorrendtől. Ez a réteg beszúrás után azonnal látszik,
			 * Chrome-ban, Safariban és Firefoxban egyaránt.
			 */
			if (!document.getElementById("kotelezo-frissites")) {
				const reteg = document.createElement("div");
				reteg.id = "kotelezo-frissites";
				reteg.setAttribute("role", "alert");
				reteg.style.cssText = [
					"position:fixed",
					"inset:0",
					"z-index:2147483647",
					"display:flex",
					"align-items:center",
					"justify-content:center",
					"background:rgba(15,23,42,0.92)",
					"color:#fff",
					"font:16px/1.6 -apple-system,Segoe UI,Arial,sans-serif",
					"text-align:center",
					"padding:24px",
				].join(";");
				reteg.innerHTML =
					'<div style="max-width:420px">' +
					'<div style="font-size:19px;font-weight:600;margin-bottom:10px">Frissítés szükséges</div>' +
					"<div>Új verzió érkezett, ami a mostani felülettel nem működik.<br>Egy pillanat, újratöltjük — nem kell semmit tenned.</div>" +
					"</div>";
				document.body.appendChild(reteg);
			}

			try {
				const reg = await navigator.serviceWorker?.getRegistration();
				await reg?.update();

				if ("caches" in window) {
					const nevek = await caches.keys();
					await Promise.all(nevek.map((n) => caches.delete(n)));
				}
			} catch {
				// ha bármi nem megy, az újratöltés akkor is megtörténik
			}

			// Rövid szünet, hogy a felhasználó lássa, mi történik.
			window.setTimeout(() => window.location.reload(), 1500);
		};

		const ellenoriz = async () => {
			try {
				const valasz = await fetch("/version.json", { cache: "no-store" });
				if (!valasz.ok) return;

				const adat = await valasz.json();

				// Előbb a kényszerítés: ha a mi kötegünk a küszöb ELŐTT készült,
				// nincs mit kérdezni.
				const kuszob: string | null = adat?.requiredBuildTime ?? null;
				const szerverBuild: string = adat?.buildTime ?? "";
				const sajatBuild = typeof __BUILD_TIME__ === "string" ? __BUILD_TIME__ : "";

				/*
				 * ⚠️ ÚJRATÖLTÉSI KÖR ELLEN KÉT VÉDELEM.
				 *
				 * Teszteléskor pontosan ez történt: a küszöb a kiszolgált
				 * kötegnél újabb volt, a felület tehát minden betöltéskor újra
				 * kényszerített — 13 újratöltés egymás után. Ez sokkal rosszabb,
				 * mint egy elavult köteg: használhatatlanná teszi a rendszert.
				 *
				 * 1. Ha a SZERVER saját kötege is régebbi a küszöbnél, akkor a
				 *    beállítás hibás (elírt küszöb, félbemaradt telepítés) —
				 *    ilyenkor nem csinálunk semmit.
				 * 2. Munkamenetenként EGYSZER kényszerítünk. Ha az újratöltés
				 *    után még mindig régi a köteg, nem próbáljuk újra, hanem a
				 *    normál, elvethető jelzésre esünk vissza.
				 */
				const kuszobErvenyes = Boolean(kuszob) && (!szerverBuild || szerverBuild >= (kuszob as string));
				let marKenyszeritettunk = false;
				try {
					marKenyszeritettunk = sessionStorage.getItem("pwa-force-done") === "1";
				} catch {
					// letiltott tároló: ilyenkor inkább NE kényszerítsünk újra
					marKenyszeritettunk = true;
				}

				if (kuszob && sajatBuild && sajatBuild < kuszob) {
					if (!kuszobErvenyes) {
						console.warn(
							"[frissítés] a kötelező küszöb újabb, mint a kiszolgált köteg — a beállítás hibás, nem kényszerítünk",
							{ kuszob, szerverBuild },
						);

						return;
					}

					if (marKenyszeritettunk) {
						// Egyszer már újratöltöttünk, és még mindig régi a köteg.
						// Ne pörögjünk: jelezzük, és hagyjuk a felhasználóra.
						if (elo) setUjVerzio(true);

						return;
					}

					try {
						sessionStorage.setItem("pwa-force-done", "1");
					} catch {
						// ha nem tudjuk megjegyezni, inkább ne kényszerítsünk
						if (elo) setUjVerzio(true);

						return;
					}

					if (elo) void kenyszeritettFrissites();

					return;
				}

				const jel = `${adat?.version ?? "?"}|${adat?.buildTime ?? "?"}`;

				if (sajatVerzio === null) {
					sajatVerzio = jel;

					return;
				}

				if (jel !== sajatVerzio && elo) setUjVerzio(true);
			} catch {
				// hálózati hiba: majd a következő körben
			}
		};

		void ellenoriz();
		const ido = setInterval(ellenoriz, 15 * 60 * 1000);

		const elotérbe = () => {
			if (document.visibilityState === "visible") void ellenoriz();
		};

		document.addEventListener("visibilitychange", elotérbe);
		window.addEventListener("focus", elotérbe);

		return () => {
			elo = false;
			clearInterval(ido);
			document.removeEventListener("visibilitychange", elotérbe);
			window.removeEventListener("focus", elotérbe);
		};
	}, []);

	const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
	const [showModal, setShowModal] = useState(false);
	// When true, ignore the 7-day dismissal — used when the user explicitly
	// asked to see the popup again from the account menu.
	const [forcedOpen, setForcedOpen] = useState(false);

	// Wire the beforeinstallprompt handler on the whole app (before AND after
	// login), and expose a global 'pwa-open-install' custom event so other
	// components (avatar menu, settings) can pop the modal on demand.
	useEffect(() => {
		if (isStandalone()) return;

		const promptHandler = (e: Event) => {
			e.preventDefault();
			setInstallEvent(e as BeforeInstallPromptEvent);
			// Auto-open only if the user hasn't dismissed recently. Even if we
			// don't auto-open, we KEEP the event so the manual trigger can fire it.
			if (!wasRecentlyDismissed()) {
				setTimeout(() => setShowModal(true), 1500);
			}
		};
		window.addEventListener("beforeinstallprompt", promptHandler);

		const forceOpenHandler = () => {
			setForcedOpen(true);
			setShowModal(true);
		};
		window.addEventListener("pwa-open-install", forceOpenHandler);

		// iOS: no beforeinstallprompt exists — but auto-show once on first open
		// if not dismissed recently.
		if (isIosSafari && !wasRecentlyDismissed()) {
			setTimeout(() => setShowModal(true), 2500);
		}

		return () => {
			window.removeEventListener("beforeinstallprompt", promptHandler);
			window.removeEventListener("pwa-open-install", forceOpenHandler);
		};
	}, []);

	/*
	 * Értesítés, ha új telepítés érkezett.
	 *
	 * Két jelzésre indul: a service worker várakozó állapotára (`needRefresh`)
	 * ÉS a version.json változására. A gomb a futó kódot is kicseréli, mert
	 * újratölt — enélkül a worker frissül, a nyitott fülön futó JavaScript
	 * viszont a régi maradna, és pont ez okozta, hogy emberek napokig régi
	 * felületen dolgoztak.
	 *
	 * Nem tűnik el magától (`duration: 0`): egy telepítés után a munka
	 * folytatható, de a jelzés ott marad, amíg rá nem kattintanak.
	 */
	useEffect(() => {
		if (!needRefresh && !ujVerzio) return;

		const key = "pwa-update";

		const frissit = async () => {
			notification.destroy(key);

			// Ha a worker várakozik, a bevált úton megyünk: SKIP_WAITING +
			// újratöltés, amit a plugin összehangoltan végez.
			if (needRefresh) {
				updateServiceWorker(true);

				return;
			}

			// Ha nincs várakozó worker (jellemzően iOS), akkor az új verziót
			// a gyorsítótárak kiürítése után töltjük be — ez az, amit eddig
			// kézzel kellett megtenni.
			try {
				const reg = await navigator.serviceWorker?.getRegistration();
				await reg?.update();

				if ("caches" in window) {
					const nevek = await caches.keys();
					await Promise.all(nevek.map((n) => caches.delete(n)));
				}
			} catch {
				// ha bármi nem megy, az újratöltés akkor is megtörténik
			}

			window.location.reload();
		};

		notification.info({
			key,
			message: "Új verzió érkezett",
			description:
				"Frissült az iroda-rendszer. Kattints a Frissítés-re — a megnyitott oldal újratöltődik, " +
				"gyorsítótárat nem kell törölni.",
			duration: 0,
			btn: (
				<Button type="primary" onClick={() => void frissit()}>
					Frissítés
				</Button>
			),
			onClose: () => {
				setNeedRefresh(false);
				setUjVerzio(false);
			},
		});
	}, [needRefresh, ujVerzio, setNeedRefresh, updateServiceWorker]);

	const dismiss = () => {
		// Only stamp the cooldown when the user closed it themselves — a forced
		// open (from the account menu) shouldn't extend the cooldown further.
		if (!forcedOpen) localStorage.setItem(DISMISS_KEY, String(Date.now()));
		setForcedOpen(false);
		setShowModal(false);
	};

	const install = async () => {
		if (!installEvent) return;
		await installEvent.prompt();
		const choice = await installEvent.userChoice;
		if (choice.outcome === "accepted") {
			setShowModal(false);
			setInstallEvent(null);
		} else {
			dismiss();
		}
	};

	if (!showModal) return null;
	// If forcedOpen (from account menu) still show the modal even if we don't
	// yet have installEvent — render a "not available" branch below so the user
	// gets feedback instead of a silently-closed action.
	if (!installEvent && !isIosSafari && !forcedOpen) return null;

	return (
		<>
			<div
				onClick={dismiss}
				onKeyDown={(e) => (e.key === "Escape" ? dismiss() : null)}
				role="button"
				tabIndex={-1}
				aria-label="Bezárás"
				style={{
					position: "fixed",
					inset: 0,
					background: "rgba(0, 0, 0, 0.55)",
					zIndex: 99998,
					backdropFilter: "blur(3px)",
					WebkitBackdropFilter: "blur(3px)",
				}}
			/>
			<div
				role="dialog"
				aria-modal="true"
				style={{
					position: "fixed",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					zIndex: 99999,
					width: "min(420px, calc(100vw - 32px))",
					background: "white",
					borderRadius: 20,
					boxShadow: "0 20px 60px rgba(0, 0, 0, 0.25)",
					overflow: "hidden",
					fontFamily: "system-ui, -apple-system, sans-serif",
				}}
			>
				<div
					style={{
						background: "linear-gradient(135deg, #004a99 0%, #0066cc 100%)",
						padding: "28px 24px 20px",
						textAlign: "center",
						color: "white",
					}}
				>
					<div
						style={{
							width: 84,
							height: 84,
							margin: "0 auto 12px",
							background: "white",
							borderRadius: 18,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
						}}
					>
						<img src="/pwa-192.png" alt="Iroda" style={{ width: 68, height: 68, borderRadius: 14 }} />
					</div>
					<div style={{ fontSize: 20, fontWeight: 700, marginBottom: 2 }}>Iroda Rendszer</div>
					<div style={{ fontSize: 13, opacity: 0.9 }}>Elektronika Vonala</div>
				</div>

				<div style={{ padding: "20px 24px 24px" }}>
					{isIosSafari ? (
						<>
							<div style={{ fontSize: 15, fontWeight: 600, color: "#111", marginBottom: 12, textAlign: "center" }}>
								Telepítsd az alkalmazást a telefonodra
							</div>
							<div style={{ fontSize: 14, color: "#555", lineHeight: 1.5, marginBottom: 16, textAlign: "center" }}>
								Gyorsabb elérés, teljes képernyős használat, saját ikon a főképernyőn.
							</div>
							<div
								style={{
									background: "#f5f7fb",
									borderRadius: 12,
									padding: 14,
									fontSize: 13,
									color: "#333",
									lineHeight: 1.6,
								}}
							>
								<div style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
									<span
										style={{
											display: "inline-flex",
											alignItems: "center",
											justifyContent: "center",
											width: 24,
											height: 24,
											background: "#004a99",
											color: "white",
											borderRadius: "50%",
											fontWeight: 700,
											fontSize: 12,
										}}
									>
										1
									</span>
									Alul koppints a{" "}
									<span
										aria-hidden
										style={{
											display: "inline-block",
											width: 18,
											height: 18,
											verticalAlign: "-4px",
											background:
												"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23007aff'%3E%3Cpath d='M12 3l4 4-1.4 1.4L13 6.83V15h-2V6.83L9.4 8.4 8 7l4-4zm7 12v4H5v-4H3v6h18v-6h-2z'/%3E%3C/svg%3E\") center/contain no-repeat",
										}}
									/>{" "}
									<strong>Megosztás</strong> ikonra
								</div>
								<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
									<span
										style={{
											display: "inline-flex",
											alignItems: "center",
											justifyContent: "center",
											width: 24,
											height: 24,
											background: "#004a99",
											color: "white",
											borderRadius: "50%",
											fontWeight: 700,
											fontSize: 12,
										}}
									>
										2
									</span>
									Válaszd a <strong>„Főképernyőhöz adás"</strong> lehetőséget
								</div>
							</div>
							<div style={{ display: "flex", gap: 8, marginTop: 20 }}>
								<Button size="large" block onClick={dismiss}>
									Rendben
								</Button>
							</div>
						</>
					) : installEvent ? (
						<>
							<div style={{ fontSize: 15, fontWeight: 600, color: "#111", marginBottom: 8, textAlign: "center" }}>
								Add hozzá a főképernyődhöz
							</div>
							<div style={{ fontSize: 14, color: "#555", lineHeight: 1.5, marginBottom: 20, textAlign: "center" }}>
								Egy koppintással elérhető, teljes képernyős használat, gyorsabb betöltés — mintha egy natív alkalmazás
								lenne.
							</div>
							<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
								<Button
									type="primary"
									size="large"
									block
									onClick={install}
									style={{ height: 46, fontWeight: 600, fontSize: 15 }}
								>
									📱 Telepítés
								</Button>
								<Button size="large" block onClick={dismiss} style={{ height: 42 }}>
									Most nem
								</Button>
							</div>
						</>
					) : (
						// Forced open on Android/desktop but the browser hasn't fired
						// beforeinstallprompt yet — either the page just loaded and
						// criteria aren't met, or the site is already installed.
						<>
							<div style={{ fontSize: 15, fontWeight: 600, color: "#111", marginBottom: 8, textAlign: "center" }}>
								A telepítés még nem elérhető
							</div>
							<div style={{ fontSize: 14, color: "#555", lineHeight: 1.5, marginBottom: 16, textAlign: "center" }}>
								A böngésző egy pillanaton belül készen áll a telepítésre. Ha ez az üzenet nem tűnik el, próbáld a
								böngésző menüjéből: <strong>Chrome / Edge menü → „Alkalmazás telepítése"</strong>. Ha már telepítve van,
								akkor kész — nyisd meg a főképernyőről.
							</div>
							<Button size="large" block onClick={dismiss}>
								Rendben
							</Button>
						</>
					)}
				</div>
			</div>
		</>
	);
}
